import { PopoverPlacement } from "@idui/react-popover";
import { MoveUp } from "@mui/icons-material";
import clsx from "clsx";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  AccessControls,
  AccessControlsMethodCheckEnum,
  FramerTooltip,
  RideFeedbacksCount,
  RidePassengersHasDog,
  UserUpdaterIndicatorRides,
} from "@ryde/controls";
import { useRideEmptyStationsAllowed } from "@ryde/redux";
import {
  distanceFormula,
  editFormDataAF,
  IRideDto,
  onActivateKeyDown,
  RideCrudModeKeyTuple,
  RideStationPanelMode,
  shouldShowUserUpdaterIndicator,
  useDrawers,
  useHasPermissions,
} from "@ryde/shared";

import { TabsBottomInfoItemStyled } from "../../../../right";
import { RideHeaderSupplierInfo } from "./ride-header-supplier-info";
import {
  HeaderLine2SkipAreaGroupSupplierIconStyled,
  HeaderLine2Styled,
} from "./ride-header.styled";
import {
  RideBoardingSegmentDurationHeaderItem,
  RideTotalDurationLabel,
} from "./ride-total-duration-indicator";

export const RideHeaderInfosLine2: FC = () => {
  const { t } = useTranslation();
  const rideDto = useRecoilValue<Partial<IRideDto> | undefined>(
    editFormDataAF(RideCrudModeKeyTuple),
  );
  const { skipSettingSupplierByAreaGroup } = useRideEmptyStationsAllowed(
    RideStationPanelMode.CRUD_TAB,
    rideDto,
  );
  const { openEditFormDrawer, openJourneyEditFormDrawer } = useDrawers();

  const isSomeHasDog = rideDto?.metadata?.passengersInfo?.some(p => p.hasDog);

  const { hasPermissions: viewVehicleTypePermission } = useHasPermissions({
    entity: "vehicleType",
    action: "read",
  });
  const { hasPermissions: viewDriverTypePermission } = useHasPermissions({
    entity: "driver",
    action: "read",
  });
  const { hasPermissions: viewJourneyTypePermission } = useHasPermissions({
    entity: "journey",
    action: "read",
  });

  const handleVehicleTypeClick = (): void => {
    const vehicleTypeId = rideDto?.vehicleTypeId;

    if (viewVehicleTypePermission && vehicleTypeId) {
      openEditFormDrawer({
        entity: "vehicleType",
        id: vehicleTypeId,
      });
    }
  };

  const handleDriverClick = (): void => {
    const driverId = rideDto?.driverId;

    if (viewDriverTypePermission && driverId) {
      openEditFormDrawer({
        entity: "driver",
        id: driverId,
      });
    }
  };

  const handleJourneyClick = (): void => {
    const journeyId = rideDto?.journeyId;

    if (viewJourneyTypePermission && journeyId) {
      openJourneyEditFormDrawer({
        id: journeyId,
      });
    }
  };

  const hasSupplier = !!rideDto?.supplierId;
  const hasVehicleType = !!rideDto?.vehicleTypeId;
  const resolvedDriverName = rideDto?.driverId
    ? rideDto.driverName || t("general.NO_NAME")
    : rideDto?.metadata?.driverInfo?.name;
  const hasDriver = !!rideDto?.driverId || !!resolvedDriverName;
  const hasJourney = !!rideDto?.journeyId;
  const hasDock = !!rideDto?.metadata?.dock;
  const totalDistance = rideDto?.metadata?.totalDistance;
  const dock = rideDto?.metadata?.dock;

  return (
    !!rideDto && (
      <HeaderLine2Styled>
        <AccessControls
          actionsList={[
            { action: "read", entity: "feedback" },
            { action: "create", entity: "feedback" },
          ]}
          methodCheck={AccessControlsMethodCheckEnum.OR}
        >
          {!!rideDto.id && (
            <TabsBottomInfoItemStyled>
              <RideFeedbacksCount {...rideDto} isCompact />
            </TabsBottomInfoItemStyled>
          )}
        </AccessControls>

        {shouldShowUserUpdaterIndicator(rideDto) && (
          <TabsBottomInfoItemStyled>
            <UserUpdaterIndicatorRides rideDto={rideDto} />
          </TabsBottomInfoItemStyled>
        )}
        {isSomeHasDog && (
          <TabsBottomInfoItemStyled>
            <RidePassengersHasDog
              passengers={rideDto?.metadata?.passengersInfo ?? []}
            />
          </TabsBottomInfoItemStyled>
        )}

        {!!totalDistance && (
          <TabsBottomInfoItemStyled>
            <span data-e2e="ride-total-distance" className="info">
              <FramerTooltip
                placement={PopoverPlacement.bottom}
                content={t("ride.formLabel.totalDistance")}
              >
                <span>
                  {distanceFormula(totalDistance)} {t("general.KM")}
                </span>
              </FramerTooltip>
            </span>
          </TabsBottomInfoItemStyled>
        )}

        <TabsBottomInfoItemStyled>
          <RideTotalDurationLabel />
        </TabsBottomInfoItemStyled>

        <RideBoardingSegmentDurationHeaderItem />

        {hasSupplier && <RideHeaderSupplierInfo rideDto={rideDto} />}

        {hasSupplier && skipSettingSupplierByAreaGroup && (
          <TabsBottomInfoItemStyled>
            <FramerTooltip
              placement={PopoverPlacement.bottom}
              content={t("rideType.formLabel.skipSettingSupplierByAreaGroup")}
            >
              <HeaderLine2SkipAreaGroupSupplierIconStyled>
                <MoveUp fontSize="inherit" />
              </HeaderLine2SkipAreaGroupSupplierIconStyled>
            </FramerTooltip>
          </TabsBottomInfoItemStyled>
        )}

        {hasVehicleType && (
          <TabsBottomInfoItemStyled className="ride-header-line2-vehicle-type">
            <FramerTooltip
              placement={PopoverPlacement.bottom}
              content={rideDto.vehicleTypeName || t("general.NO_NAME")}
            >
              <div className="item__content truncate-text">
                <label className="label">{t("vehicleType.single")}</label>
                <span
                  data-e2e="ride-vehicle-type"
                  className={clsx({
                    info: true,
                    pointer: viewVehicleTypePermission,
                  })}
                  role="button"
                  tabIndex={0}
                  onClick={handleVehicleTypeClick}
                  onKeyDown={onActivateKeyDown(handleVehicleTypeClick)}
                >
                  {rideDto.vehicleTypeName || t("general.NO_NAME")}
                </span>
              </div>
            </FramerTooltip>
          </TabsBottomInfoItemStyled>
        )}
        {!!rideDto.id && rideDto.metadata?.seatsCountByDriver !== undefined && (
          <TabsBottomInfoItemStyled>
            <FramerTooltip
              placement={PopoverPlacement.bottom}
              content={t("ride.gridLabel.seatsCountByDriver")}
            >
              <span>
                {t("ride.gridLabel.seatsCountByDriver")}:{" "}
                {rideDto.metadata?.seatsCountByDriver ?? 0}
              </span>
            </FramerTooltip>
          </TabsBottomInfoItemStyled>
        )}

        {hasDriver && (
          <TabsBottomInfoItemStyled className="ride-header-line2-driver">
            <FramerTooltip
              placement={PopoverPlacement.bottom}
              content={resolvedDriverName}
            >
              <div className="item__content truncate-text">
                <label className="label">{t("driver.single")}</label>
                <span
                  data-e2e="ride-driver"
                  className={clsx({
                    info: true,
                    pointer: viewDriverTypePermission && !!rideDto?.driverId,
                  })}
                  role="button"
                  tabIndex={0}
                  onClick={handleDriverClick}
                  onKeyDown={onActivateKeyDown(handleDriverClick)}
                >
                  {resolvedDriverName}
                </span>
              </div>
            </FramerTooltip>
          </TabsBottomInfoItemStyled>
        )}

        {hasDock && dock && (
          <TabsBottomInfoItemStyled>
            <FramerTooltip placement={PopoverPlacement.bottom} content={dock}>
              <div className="item__content truncate-text">
                <label className="label">{t("ride.formLabel.dock")}</label>
                <label data-e2e="ride-dock" className="info">
                  {dock}
                </label>
              </div>
            </FramerTooltip>
          </TabsBottomInfoItemStyled>
        )}

        {hasJourney && (
          <TabsBottomInfoItemStyled>
            <FramerTooltip
              placement={PopoverPlacement.bottom}
              content={rideDto.journeyName || t("general.NO_NAME")}
            >
              <div className="item__content truncate-text">
                <label className="label">{t("ride.formLabel.journey")}</label>
                <span
                  data-e2e="ride-journey"
                  className={clsx({
                    info: true,
                    pointer: viewJourneyTypePermission,
                  })}
                  role="button"
                  tabIndex={0}
                  onClick={handleJourneyClick}
                  onKeyDown={onActivateKeyDown(handleJourneyClick)}
                >
                  {rideDto.journeyName || t("general.NO_NAME")}
                </span>
              </div>
            </FramerTooltip>
          </TabsBottomInfoItemStyled>
        )}
      </HeaderLine2Styled>
    )
  );
};
